import type { OfferPrice, SavedPackageOffer } from './types';

/**
 * A Regularización total cart, read from its lines. The engine builds it: a
 * container line that charges the package, one $0 line per period (the ones
 * delivery marks as acquired) and the plan's first month, every line stamped
 * with `product_package`.
 */

export const PONTE_AL_DIA_PACKAGE = 'ponte_al_dia_v1';

type LineInfo = Record<string, unknown> | null | undefined;

/** Any line of the package: container, period or plan. */
export const isPackageLine = (additionalInformation: unknown): boolean =>
	(additionalInformation as LineInfo)?.product_package === PONTE_AL_DIA_PACKAGE;

/** The plan line the package carries: shown apart, it is half of what was bought. */
export const isPackagePlanLine = (additionalInformation: unknown): boolean =>
	isPackageLine(additionalInformation) &&
	(additionalInformation as LineInfo)?.package_line === 'plan_first_month_free';

/** A $0 period line: delivery marks it acquired; the summary does not list it. */
export const isPackagePeriodLine = (additionalInformation: unknown): boolean => {
	if (!isPackageLine(additionalInformation)) return false;
	const line = (additionalInformation as LineInfo)?.package_line;
	return line === 'month' || line === 'annual';
};

/**
 * The offer the engine saved on the container line when it built the cart, as
 * the screens render it. Checkout, the purchase screen and the plans tab read
 * the price from here instead of adding up cart lines.
 *
 * `null` for any other cart and for package carts built before the engine
 * saved the offer: those show the cart's own totals.
 */
export const savedPackageOfferOf = (
	items: readonly unknown[] | null | undefined,
): OfferPrice | null => {
	for (const item of items ?? []) {
		const info = (item as { additional_information?: LineInfo })?.additional_information;
		if (info?.package_line !== 'container') continue;
		const saved = info?.offer as SavedPackageOffer | undefined;
		if (!Array.isArray(saved?.lines)) return null;
		return {
			lines: saved.lines.map(({ renews_cents, ...line }) =>
				renews_cents === undefined ? line : { ...line, then_monthly_cents: renews_cents },
			),
			total: {
				list_cents: saved.list_total_cents,
				savings_cents: saved.savings_cents,
				discount_pct: saved.discount_pct,
				charge_cents: saved.total_cents,
			},
		};
	}
	return null;
};

/**
 * What the cart takes off beyond its saved offer. The offer is the price when
 * the cart was built; a coupon applied in checkout afterwards lowers the cart
 * without touching it. Shown as its own discount row, it is what makes the
 * rows of a package cart (the offer's package and plan lines, that row and
 * heru-cash) add up to what the cart charges. 0 when nothing changed.
 */
export const discountBeyondOfferCents = (
	price: OfferPrice,
	cart: { total?: number | null; credits_discount?: number | null },
): number => {
	const offered = price.lines
		.filter((line) => line.kind !== 'credits')
		.reduce((charged, line) => charged + line.final_cents, 0);
	return Math.max(0, offered - (cart.credits_discount ?? 0) - (cart.total ?? 0));
};
